import clsx from "clsx";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateContactStateMutation } from "../../../../../features/contactsApi";
import { addBannerWithTimeout } from "../../../../lib/centralizedBanner/slice";
import { ContactDTO, ContactType } from "../../../../lib/filters/dto";
import { keycloak } from "../../../../lib/keycloak";
import { useAppDispatch, useAppSelector } from "../../../../lib/redux/hooks";
import { CheckCircleIcon } from "../../../../ui/icons/checkCircle";
import { EditIcon } from "../../../../ui/icons/edit";
import { GlobeIcon } from "../../../../ui/icons/globe";
import { LinkedinIcon } from "../../../../ui/icons/linkedin";
import { MailIcon } from "../../../../ui/icons/mail";
import { PhoneCallIcon } from "../../../../ui/icons/phoneCall";
import { RotateIcon } from "../../../../ui/icons/rotate";
import { Badge } from "../../../../ui/molecules/badge";
import Button from "../../../../ui/molecules/button";
import { GroupSelectCustom } from "../../../../ui/molecules/groupSelect";
import { CompanyDTO } from "../../../database/companies/dto";
import { PersonDTO } from "../../../database/people/dto";
import { ProjectDTO } from "../../../projects/dto";
import { EmailsMessagesDTO } from "../../../projects/mails/dto";
import { setSendEmailModalOpen } from "../../../projects/mails/slice";
import { ContactStateEnum, generateDefaultValue, generateOutcomeOptions } from "../../dto";
import { ContactEmployeesResponse } from "../dto";
import { LeadTakenModal } from "../leadTakenModal";
import { PlanRecallModal } from "../recall/planRecallModal";
import { setOperationType } from "../recall/slice";
import { setShowRecallContactModal } from "../slice";

type Props = (
    {
        title: string
        contact: ContactDTO<ContactType>
        contactEmployees?: ContactEmployeesResponse
        project: ProjectDTO
        emailsHistory: { messages: EmailsMessagesDTO[] }
    } & ({
        contactType: ContactType.COMPANY;
        contactData?: CompanyDTO
        listIndex: number;
    } | {
        contactType: ContactType.PERSON;
        contactData?: PersonDTO;
        listIndex: number;
    }))

export function ContactForm(props: Props) {
    const { contactType, contactData, listIndex, title, contact, contactEmployees, project, emailsHistory } = props
    const { targetId, projectId } = useParams();
    const recallsState = useAppSelector(state => state.recalls)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [confirmLeadTaken, setConfirmLeadTaken] = useState(false)
    const [updateContactState, { isLoading: isUpdatingContactState }] = useUpdateContactStateMutation();

    const handleUpdateContactState = async (newContactState: ContactStateEnum, newOutcomeInfo: string | null) => {
        try {
            await updateContactState({
                contactId: contact?.id!,
                outcome: newContactState,
                contactType,
                outcomeInfo: newOutcomeInfo,
                targetId: targetId!
            }).unwrap();
            setConfirmLeadTaken(false);
            dispatch(addBannerWithTimeout({
                variant: "success",
                message: "Stato del contatto aggiornato con successo.",
            }));
        } catch (error) {
            const res = error as unknown as { status: number, data: { errors: { message: string }[] } };
            let message = "Si è verificato un errore durante l'aggiornamento dello stato del contatto.";
            const errors = res?.data?.errors ?? [];
            if (errors?.some(e => e.message.includes("has already reached the maximum number of calls"))) {
                message = "Il contatto ha già raggiunto il numero massimo di chiamate consentite.";
            } else if (errors?.some(e => e.message.includes("Contact ID is required"))) {
                message = "Non è possibile prendere il Lead perché non ancora presente in Lista Target. Riprova domani.";
            } else if (errors?.some(e => e.message.includes("Invalid transition from DRAFT to DRAFT"))) {
                message = "Il contatto è già nello stato 'Da Lavorare'";
            }
            dispatch(addBannerWithTimeout({
                variant: "error",
                message: message,
            }));
        }
    }

    if (!contactData) {
        return <></>
    }

    let emailsToCheck: string[] = []

    if (contactType === ContactType.PERSON) {
        emailsToCheck = contactData.emails || []
    } else {
        emailsToCheck = contactEmployees?.employeesContacts?.flatMap(employee => employee?.contact?.emails) || []
        emailsToCheck = [...emailsToCheck, ...(contactData.emails || [])]
        emailsToCheck = [...emailsToCheck, ...(contact?.sendEmails?.map(email => email) || [])]
    }

    return (
        <>
            <div className="w-full px-6 py-3 flex items-center border-y border-y-brandPrimary-100 justify-between mt-1 bg-brandPrimary-50">
                <div className="gap-2 flex">
                    <div className=" relative">
                        <Button
                            disabled={
                                keycloak.hasRealmRole('customer') ||
                                keycloak.hasRealmRole('collaborator')
                                // ||
                                // contactType === ContactType.PERSON && (
                                //     contactData.emails === null ||
                                //     contactData.emails.length === 0 ||
                                //     contactData.emails.filter(email => email !== null).length === 0
                                // )
                            }
                            size={"sm"}
                            iconPosition={"only"}
                            variant={"outline"}
                            onClick={() => {
                                dispatch(setSendEmailModalOpen(true))
                            }}
                            color={"blue"}
                            icon={<MailIcon color={""} size={0} />}
                        />
                        <div
                            className={clsx("absolute top-[-20%] right-[-8%]", {
                                hidden: contactData.emails === null || contactData.emails.length === 0 || emailsHistory === undefined || emailsHistory?.messages?.flatMap(item => item).filter(item => item.opens_count && emailsToCheck.includes(item.to_email)).length === 0
                            })}
                        >
                            <Badge
                                color={
                                    (emailsHistory?.messages?.flatMap(item => item).filter(item => item.opens_count && emailsToCheck.includes(item.to_email)).length > 0) ? 'green' : "yellow"}
                                label={''}
                            />
                        </div>
                    </div>
                    <Button
                        disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                        size={"sm"}
                        iconPosition={"only"}
                        variant={"outline"}
                        color={"blue"}
                        onClick={() => {
                            const a = document.createElement("a");
                            document.body.appendChild(a);
                            a.rel = "noreferrer"
                            a.target = "_blank"
                            a.href = 'tel:' + (contactType === ContactType.PERSON ? contactData.phone : contactData.phones[0]);
                            a.click();
                        }}
                        icon={<PhoneCallIcon color={""} size={0} />}
                    />
                    {
                        contactType === ContactType.COMPANY &&
                        <Button
                            disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                            size={"sm"}
                            iconPosition={"only"}
                            variant={"outline"}
                            color={"blue"}
                            onClick={() => {
                                const a = document.createElement("a");
                                document.body.appendChild(a);
                                a.rel = "noreferrer"
                                a.target = "_blank"
                                a.href = contactData.website!;
                                a.click();
                            }}
                            icon={<GlobeIcon color={""} size={0} />}
                        />
                    }
                    {
                        contactType === ContactType.PERSON &&
                        <Button
                            disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                            size={"sm"}
                            iconPosition={"only"}
                            variant={"outline"}
                            color={"blue"}
                            onClick={() => {
                                const a = document.createElement("a");
                                document.body.appendChild(a);
                                a.rel = "noreferrer"
                                a.target = "_blank"
                                a.href = contactData.linkedinProfile!;
                                a.click();
                            }}
                            icon={<LinkedinIcon color={""} size={0} />}
                        />
                    }
                    <Button
                        disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                        size={"sm"}
                        iconPosition={"only"}
                        variant={"outline"}
                        color={"blue"}
                        onClick={() => {
                            navigate(`/edit-target-list/${targetId}/${contactType === ContactType.PERSON ? 'people' : 'companies'}/${projectId}/${contactData?.id}/notes/${contact?.id}`)
                        }}
                        icon={<EditIcon color={""} size={0} />}
                    />
                    <Button
                        disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                        size={"sm"}
                        iconPosition={"only"}
                        variant={"outline"}
                        color={"blue"}
                        onClick={() => {
                            dispatch(setOperationType('save'))
                            dispatch(setShowRecallContactModal(true))
                        }}
                        icon={<RotateIcon color={""} size={0} />}
                    />
                </div>
                {
                    (
                        contactType === ContactType.PERSON ||
                        (contactType === ContactType.COMPANY && contact?.outcome === ContactStateEnum.LEADTAKEN) ||
                        (contactType === ContactType.COMPANY && contactEmployees?.employeesContacts.some(employee => employee.outcome === ContactStateEnum.LEADTAKEN))
                    ) &&
                    <Button
                        size={"sm"}
                        disabled={
                            keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator') || contact?.outcome === ContactStateEnum.LEADTAKEN ||
                            (contactType === ContactType.COMPANY && contactEmployees?.employeesContacts.some(employee => employee.outcome === ContactStateEnum.LEADTAKEN))
                        }
                        iconPosition={contact?.outcome === ContactStateEnum.LEADTAKEN ? "left" : 'off'}
                        variant={contact?.outcome === ContactStateEnum.LEADTAKEN || (contactType === ContactType.COMPANY && contactEmployees?.employeesContacts.some(employee => employee.outcome === ContactStateEnum.LEADTAKEN)) ? "solid" : "outline"}
                        label="Lead preso"
                        color={contact?.outcome === ContactStateEnum.LEADTAKEN || (contactType === ContactType.COMPANY && contactEmployees?.employeesContacts.some(employee => employee.outcome === ContactStateEnum.LEADTAKEN)) ? "green" : 'gray'}
                        icon={<CheckCircleIcon color={""} size={0} />}
                        onClick={() => { if (contact?.outcome !== ContactStateEnum.LEADTAKEN) setConfirmLeadTaken(true) }}
                    />
                }
            </div>
            <div className="w-full px-6 py-3 flex items-center border-b border-b-brandPrimary-100 justify-between mt-1">
                <GroupSelectCustom
                    disabled={contactData.callsCounter >= 3 || keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                    menuPortalTargetIsNotBody
                    placeholder={"Seleziona un esito"}
                    label="Esito"
                    onChange={(e) => {
                        if (e === ContactStateEnum.DRAFT || e === ContactStateEnum.LEADTAKEN) {
                            handleUpdateContactState(e, null);
                        } else {
                            handleUpdateContactState(e && e !== undefined && typeof e === 'string' ? e.split('::')[0] as ContactStateEnum : ContactStateEnum.DRAFT, e && e !== undefined && typeof e === 'string' ? e.split('::')[1] : null);
                        }
                    }}
                    defaultValue={generateDefaultValue(contact?.outcome || ContactStateEnum.DRAFT, contact?.outcomeInfo ?? null)}
                    options={generateOutcomeOptions(contact?.callAttempts!, contact?.noResponseCount!, contact?.outcome || ContactStateEnum.DRAFT, contactType)}
                />
            </div>
            <LeadTakenModal
                person={contactData as PersonDTO}
                isLoading={isUpdatingContactState}
                handleConfirm={handleUpdateContactState}
                listIndex={listIndex}
                isOpen={confirmLeadTaken}
                onClose={function (): void {
                    setConfirmLeadTaken(false);
                }}
            />
            {recallsState.operationType === 'save' && <PlanRecallModal targetId={targetId!} project={project} contactId={contact?.id!} title={title} contactType={contactType} type={'save'} data={undefined} />}
        </>
    )
}